/**
 * Export session data to CSV format
 */
export function exportSessionToCSV(sessionData) {
  const lines = [];

  // Header
  lines.push(`Session: ${sessionData.name}`);
  lines.push(`Date: ${sessionData.date}`);
  lines.push('');

  // For each shot group
  sessionData.shotGroups.forEach(group => {
    lines.push(`Shot Group: ${group.name}`);
    lines.push('Shot,Included,Club Speed,Ball Speed,Smash Factor,Launch Angle,Spin Rate,Carry,Total,Landing Angle,Height,Attack Angle,Offline');

    group.shots.forEach(shot => {
      const m = shot.metrics;
      lines.push([
        shot.shotNumber,
        shot.included ? 'Yes' : 'No',
        m.clubSpeed?.toFixed(1) || '',
        m.ballSpeed?.toFixed(1) || '',
        m.smashFactor?.toFixed(2) || '',
        m.launchAngle?.toFixed(1) || '',
        m.spinRate?.toFixed(0) || '',
        m.carry?.toFixed(1) || '',
        m.total?.toFixed(1) || '',
        m.landingAngle?.toFixed(1) || '',
        m.height?.toFixed(0) || '',
        m.attackAngle?.toFixed(1) || '',
        m.offlineDistance?.toFixed(1) || '',
      ].join(','));
    });

    lines.push('');
  });

  // Create and download file
  const csvContent = lines.join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', `${sessionData.name.replace(/[^a-z0-9]/gi, '_')}_${sessionData.date}.csv`);
  link.style.visibility = 'hidden';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * Copy current page URL to clipboard for sharing
 */
export async function shareSession(sessionData) {
  const url = window.location.href;

  try {
    await navigator.clipboard.writeText(url);
    return { success: true, message: 'Link copied to clipboard!' };
  } catch (err) {
    // Fallback for older browsers
    const textArea = document.createElement('textarea');
    textArea.value = url;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      document.execCommand('copy');
      document.body.removeChild(textArea);
      return { success: true, message: 'Link copied to clipboard!' };
    } catch (err2) {
      document.body.removeChild(textArea);
      return { success: false, message: 'Failed to copy link' };
    }
  }
}

/**
 * Generate a summary report as plain text
 */
export function generateTextReport(sessionData) {
  const lines = [];

  lines.push('='.repeat(60));
  lines.push(`GOLF SESSION REPORT: ${sessionData.name}`);
  lines.push(`Date: ${sessionData.date}`);
  lines.push('='.repeat(60));
  lines.push('');

  sessionData.shotGroups.forEach(group => {
    const includedShots = group.shots.filter(s => s.included);

    if (includedShots.length === 0) return;

    // Calculate averages
    const avg = (key) => {
      const sum = includedShots.reduce((s, shot) => s + (shot.metrics[key] || 0), 0);
      return sum / includedShots.length;
    };

    lines.push(`\n${group.name}${group.isBaseline ? ' (BASELINE)' : ''}`);
    lines.push('-'.repeat(60));
    lines.push(`Shots: ${includedShots.length}/${group.shots.length} included`);
    lines.push('');
    lines.push('AVERAGES:');
    lines.push(`  Club Speed:    ${avg('clubSpeed').toFixed(1)} mph`);
    lines.push(`  Ball Speed:    ${avg('ballSpeed').toFixed(1)} mph`);
    lines.push(`  Smash Factor:  ${avg('smashFactor').toFixed(2)}`);
    lines.push(`  Launch Angle:  ${avg('launchAngle').toFixed(1)}°`);
    lines.push(`  Spin Rate:     ${avg('spinRate').toFixed(0)} rpm`);
    lines.push(`  Carry:         ${avg('carry').toFixed(1)} yards`);
    lines.push(`  Total:         ${avg('total').toFixed(1)} yards`);
    lines.push(`  Landing Angle: ${avg('landingAngle').toFixed(1)}°`);
    lines.push(`  Height:        ${avg('height').toFixed(0)} feet`);
    lines.push(`  Attack Angle:  ${avg('attackAngle').toFixed(1)}°`);
    lines.push('');
  });

  lines.push('='.repeat(60));
  lines.push('Generated by PocketFitter');
  lines.push('='.repeat(60));

  return lines.join('\n');
}

/**
 * Download text report
 */
export function downloadTextReport(sessionData) {
  const report = generateTextReport(sessionData);
  const blob = new Blob([report], { type: 'text/plain;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', `${sessionData.name.replace(/[^a-z0-9]/gi, '_')}_report.txt`);
  link.style.visibility = 'hidden';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}
